import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../api/axios";
import Pagination from "../../components/Pagination";
import SortableTableHeader from "../../components/SortableTableHeader";

const inputCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
const labelCls =
  "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

const BuyBFMReport = () => {
  const token = useSelector((state) => state.auth.access_token);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    username: "",
    page: 1,
    pageSize: 30,
    startDate: "",
    EndDate: "",
    order: "",
  });
  const [tempFilters, setTempFilters] = useState({
    username: "",
    page: 1,
    pageSize: 30,
    startDate: "",
    EndDate: "",
    order: "",
  });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [sortedData, setSortedData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.username) params.username = filters.username;
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.EndDate) params.EndDate = filters.EndDate;
      if (filters.order) params.order = filters.order;
      const res = await axiosInstance.get("/api/admin/reports/bfm-buy-history", { params, headers: { token } });
      const newData = res.data.data.data;
      setData(newData);
      setSortedData(newData);
      setPagination({
        currentPage: Number(filters.page) || 1,
        totalPages: res.data.data.totalPages || 1,
        totalRows: res.data.data.totalRows || 0,
      });
    } catch (err) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key];
      let bValue = b[sort.key];
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";
      if (sort.key === "amount") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (sort.key === "createdAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => setFilters({ ...tempFilters });

  const clearFilters = () => {
    const d = { username: "", page: 1, pageSize: 30, startDate: "", EndDate: "", order: "" };
    setTempFilters(d);
    setFilters(d);
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filterByToday = () => {
    const today = new Date().toISOString().split("T")[0];
    setTempFilters((prev) => ({ ...prev, startDate: today, EndDate: today, page: 1 }));
  };

  const filterByWeek = () => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 6);
    setTempFilters((prev) => ({ ...prev, startDate: weekAgo.toISOString().split("T")[0], EndDate: today.toISOString().split("T")[0], page: 1 }));
  };

  const exportToExcel = async () => {
    try {
      const params = {};
      if (filters.username) params.username = filters.username;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.EndDate) params.EndDate = filters.EndDate;
      if (filters.order) params.order = filters.order;
      params.pageSize = 10000;
      params.page = 1;
      const res = await axiosInstance.get("/api/admin/reports/bfm-buy-history", { params, headers: { token } });
      const exportData = res.data.data.data;
      if (!exportData.length) { alert("No data to export"); return; }
      const headers = ["Username", "Amount", "Message", "Created At"];
      const csvContent = [
        headers.join(","),
        ...exportData.map((row) =>
          [row.username || "", row.amount || "", row.message || "", new Date(row.createdAt).toLocaleString()].join(",")
        ),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.setAttribute("href", URL.createObjectURL(blob));
      link.setAttribute("download", `buy_bfm_report_${new Date().toISOString().split("T")[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert("Export failed. Please try again.");
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Reports
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Buy <span className="text-gradient-ginox">BFM</span>
      </h1>

      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Username / WalletAddress</label>
            <input type="text" name="username" value={tempFilters.username} onChange={handleTempFilterChange} placeholder="Search…" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" name="startDate" value={tempFilters.startDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input type="date" name="EndDate" value={tempFilters.EndDate} onChange={handleTempFilterChange} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Order</label>
            <select name="order" value={tempFilters.order} onChange={handleTempFilterChange} className={inputCls}>
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select name="pageSize" value={tempFilters.pageSize} onChange={handleTempFilterChange} className={inputCls}>
              {[10, 15, 20, 25, 30, 50].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="button" onClick={applyFilters} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors">
            Apply Filters
          </button>
          <button type="button" onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
          <button type="button" onClick={filterByToday} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Today
          </button>
          <button type="button" onClick={filterByWeek} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            This Week
          </button>
          <button type="button" onClick={exportToExcel} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort} className="text-left">S.No</SortableTableHeader>
              <SortableTableHeader sortKey="username" currentSort={sort} onSort={handleSort} className="text-left">Username</SortableTableHeader>
              <SortableTableHeader sortKey="amount" currentSort={sort} onSort={handleSort} className="text-left">Amount</SortableTableHeader>
              <SortableTableHeader sortKey="message" currentSort={sort} onSort={handleSort} className="text-left">Message</SortableTableHeader>
              <SortableTableHeader sortKey="createdAt" currentSort={sort} onSort={handleSort} className="text-left">Created At</SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
            ) : sortedData.length === 0 ? (
              <tr><td colSpan={5} className="py-12 text-center text-sm text-muted-foreground">No data found</td></tr>
            ) : (
              sortedData.map((row, index) => (
                <tr key={row._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{(filters.page - 1) * filters.pageSize + index + 1}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.username}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{row.amount}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{row.message}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{new Date(row.createdAt).toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalRows={pagination.totalRows}
        pageSize={filters.pageSize}
        onPageChange={(newPage) => setFilters((prev) => ({ ...prev, page: newPage }))}
      />
    </div>
  );
};

export default BuyBFMReport;
